import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    AMOUNT_PRINCIPAL_THRESHOLD,
    getAmountMaxValue,
    isAmountOverMax,
} from '../../components/validations/validations';
import { getUserNotificationsCount } from '../../services/login-service';
import {
    createBulkStripePaymentIntents,
    makeBulkPayment,
    getStripePaymentMethods,
    StripePaymentMethodItem,
    CreateBulkPaymentResponse,
} from '../../services/payment-services';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { setUserNotificationsCount } from '../../store/reducers/userSlice';
import { trackPaymentCompleted, trackPaymentFailed, trackPaymentInitiated } from '../../utils/google-analytics';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useStripeInstance } from '../useStripeInstance';

import type { PaymentRow } from './usePaymentRows';
import type { ContractApiResponse } from '../../services/contract-services';
import type { ImpersonationState } from '../../store/reducers/impersonationSlice';
import type { Dispatch, SetStateAction } from 'react';

interface UsePaymentSubmissionParams {
    paymentRows: PaymentRow[];
    activeContracts: ContractApiResponse[];
    getContractById: (contractId: string) => ContractApiResponse | null;
    getEffectivePaymentAmount: (row: PaymentRow, contract: ContractApiResponse | null | undefined) => number;
    getDbContractIdForPayload: (row: PaymentRow) => string;
    paymentDatePickerErrors: string[];
    setPaymentRowErrors: Dispatch<
        SetStateAction<Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>>
    >;
    setPaymentSubmitErrors: Dispatch<SetStateAction<Array<string | null>>>;
    /** Re-fetches contracts/open-invoices/scheduled-payments; called after a partial bulk-card-payment outcome so due amounts reflect reality without navigating away. */
    refreshPaymentData: () => Promise<void>;
    userDetails: Record<string, unknown> | null | undefined;
    savedPaymentMethods: StripePaymentMethodItem[];
    cardAdditionalFees?: number;
    t: (key: string) => string;
    impersonation?: ImpersonationState | null;
}

/**
 * Owns the payment-submission flow for both bank-account (bulk payment) and card (Stripe payment
 * intents) methods: the "Pay" click validation, the confirmation modal, and the final submit.
 */
export const usePaymentSubmission = ({
    paymentRows,
    activeContracts,
    getContractById,
    getEffectivePaymentAmount,
    getDbContractIdForPayload,
    paymentDatePickerErrors,
    setPaymentRowErrors,
    setPaymentSubmitErrors,
    refreshPaymentData,
    userDetails,
    savedPaymentMethods,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    cardAdditionalFees = 0,
    t,
    impersonation = null,
}: UsePaymentSubmissionParams) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const stripePromise = useStripeInstance();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    // Whether this submission covers more than one contract - drives the `payment_type` analytics
    // param across every tracking call below (single source of truth, computed once per render).
    const paymentType = paymentRows.length > 1 ? 'bulk' : 'one_time';

    // Shared by handleMakePayment and handlePayWithCard: builds the per-row required-field error
    // map. The bank-account-type field differs between bank-account and card flows, so the caller
    // supplies how to compute it; the payment-date/amount checks are identical in both flows.
    const buildRequiredFieldErrors = (
        getBankAccountFieldError: (row: PaymentRow) => boolean,
    ): Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }> => {
        return paymentRows.map((row, rowIndex) => {
            const contract = activeContracts.find(
                (c: ContractApiResponse) => c.Id?.toString() === row.dbContractId || c.AspireId === row.contractId,
            );
            const amount = getEffectivePaymentAmount(row, contract ?? undefined);
            const amountInvalid = !Number.isFinite(amount) || amount <= 0;
            const paymentDateInvalid = !row?.paymentDate || Boolean(paymentDatePickerErrors[rowIndex]);
            return {
                bankAccount: getBankAccountFieldError(row),
                paymentDate: paymentDateInvalid,
                paymentAmount: amountInvalid,
            };
        });
    };

    // Shared by handleMakePayment and handlePayWithCard: shows the required-fields error toast and
    // records row errors when any row is invalid. Returns true when the rows are all valid.
    const validateRequiredFieldsOrShowError = (
        requiredFieldErrors: Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>,
    ): boolean => {
        const hasRequiredErrors = requiredFieldErrors.some(e => e.bankAccount || e.paymentDate || e.paymentAmount);
        if (hasRequiredErrors) {
            setPaymentRowErrors(requiredFieldErrors);
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Please Fill All Required Fields To Continue With Your Payment.',
                    severity: 'error',
                }),
            );
            return false;
        }
        return true;
    };

    // Shared by handleMakePayment, handlePayWithCard, and handleConfirmPayment: validates that
    // EVERY row's amount is within the allowed max (outstanding balance; cap at 99,999,999.99 if
    // balance >= 100M) — checks all rows instead of stopping at the first bad one, and flags each
    // offending row individually via the inline paymentSubmitErrors banner (not paymentRowErrors:
    // that flag also gates the Pay button, and for card rows the amount is fixed/non-editable, so
    // there'd be no way for the user to ever clear it and Pay would stay disabled forever).
    const validateAllRowAmountsWithinMax = (rows: PaymentRow[]): boolean => {
        const rowErrorMessages: Array<string | null> = rows.map(row => {
            const contract = activeContracts.find(
                (c: ContractApiResponse) => c.Id?.toString() === row.dbContractId || c.AspireId === row.contractId,
            );
            if (!contract) return null;
            const outstandingBalance = Number(contract.OutstandingBalance) || Number(contract.RemainingPrincipal) || 0;
            const effectiveMax = getAmountMaxValue(outstandingBalance);
            const amount = getEffectivePaymentAmount(row, contract);
            if (isAmountOverMax(amount, effectiveMax)) {
                return outstandingBalance >= AMOUNT_PRINCIPAL_THRESHOLD
                    ? 'Amount Cannot Exceed 99,999,999.99'
                    : 'The Amount Cannot Exceed The Outstanding Balance.';
            }
            return null;
        });

        const invalidCount = rowErrorMessages.filter(Boolean).length;
        if (invalidCount === 0) return true;

        setPaymentSubmitErrors(prev => rows.map((_, i) => rowErrorMessages[i] ?? prev[i] ?? null));
        dispatch(
            openToast({
                show: true,
                isAlertOpen: true,
                message:
                    invalidCount === 1
                        ? (rowErrorMessages.find(Boolean) ?? 'The Amount Cannot Exceed The Outstanding Balance.')
                        : `${invalidCount} payment(s) exceed the allowed amount.`,
                severity: 'error',
            }),
        );
        return false;
    };

    // Card payment specific: credit card payment is only for contracts without AutoPay enabled —
    // AutoPay contracts must use bank transfer instead. This is a submit-time backstop; the primary
    // guard is isCardUnavailableForContract (useCardFees.ts), which keeps AutoPay contracts out of
    // the card-payment row list in the first place.
    const validateNoAutoPayForCardPayment = (rows: PaymentRow[]): boolean => {
        const autoPayRowIndexes: number[] = [];
        rows.forEach((row, index) => {
            const contract = activeContracts.find(
                (c: ContractApiResponse) => c.Id?.toString() === row.dbContractId || c.AspireId === row.contractId,
            );
            if (contract?.AutoPay) autoPayRowIndexes.push(index);
        });
        if (autoPayRowIndexes.length === 0) return true;

        const message = 'Credit card payment is not available for contracts with AutoPay enabled.';
        setPaymentSubmitErrors(prev => {
            const next = rows.map((_, i) => prev[i] ?? null);
            autoPayRowIndexes.forEach(i => {
                next[i] = message;
            });
            return next;
        });
        dispatch(
            openToast({
                show: true,
                isAlertOpen: true,
                message,
                severity: 'error',
            }),
        );
        return false;
    };

    // Card payment specific: validates that each row meets the minimum $300 payment amount
    const validateCardPaymentMinimumAmount = (rows: PaymentRow[]): boolean => {
        const MINIMUM_CARD_PAYMENT = 300;
        for (const row of rows) {
            const contract = activeContracts.find(
                (c: ContractApiResponse) => c.Id?.toString() === row.dbContractId || c.AspireId === row.contractId,
            );
            const amount = getEffectivePaymentAmount(row, contract ?? undefined);
            if (amount < MINIMUM_CARD_PAYMENT) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: t('makePayment.cardMinimumPaymentRequired'),
                        severity: 'error',
                    }),
                );
                return false;
            }
        }
        return true;
    };

    const handleMakePayment = () => {
        // Validate required fields: every row must have bank account, payment date, and amount > 0
        const requiredFieldErrors = buildRequiredFieldErrors(row => !row?.bankAccount || row.bankAccount === '');
        if (!validateRequiredFieldsOrShowError(requiredFieldErrors)) {
            return;
        }
        setPaymentRowErrors([]);

        // Validate each row: amount cannot exceed allowed max (outstanding balance; cap at 99,999,999.99 if balance >= 100M)
        if (!validateAllRowAmountsWithinMax(paymentRows)) {
            return;
        }

        // Track payment initiation for each contract
        paymentRows.forEach(row => {
            const contract = activeContracts.find(
                (c: ContractApiResponse) => c.Id?.toString() === row.dbContractId || c.AspireId === row.contractId,
            );
            trackPaymentInitiated(getEffectivePaymentAmount(row, contract ?? undefined), row.contractId, paymentType);
        });

        setShowConfirmationModal(true);
    };

    // Shared by handlePayWithCard's two success paths (no intents to confirm, and after Stripe
    // confirms all payments): tracks completion analytics and shows the success toast.
    const trackAndShowCardPaymentSuccess = () => {
        paymentRows.forEach(row => {
            const contract = getContractById(row.contractId) || getContractById(row.dbContractId);
            trackPaymentCompleted(
                getEffectivePaymentAmount(row, contract ?? undefined),
                row.contractId,
                'Card',
                paymentType,
            );
        });
        dispatch(
            openToast({
                show: true,
                isAlertOpen: true,
                message:
                    paymentRows.length === 1
                        ? t('makePayment.paymentCompletedSuccessSingle')
                        : t('makePayment.paymentCompletedSuccessMultiple').replace('%d', String(paymentRows.length)),
                severity: 'success',
            }),
        );
        setTimeout(() => navigate(NavigationConfig.Payments, { state: { activeTab: 1 } }), 1500);
    };

    // Maps the backend's per-payment validation errors (index/contractId + message) onto the
    // paymentRows array so each failing row can show its specific reason instead of one generic
    // toast. Falls back to matching by contractId when index is missing/out of range.
    const mapBackendErrorsToRows = (
        errors: Array<{ index?: number; contractId?: string | number; error?: string }> | undefined,
    ): Array<string | null> => {
        const next: Array<string | null> = paymentRows.map(() => null);
        if (!Array.isArray(errors)) return next;
        for (const err of errors) {
            if (!err?.error) continue;
            let rowIndex = -1;
            if (typeof err.index === 'number' && err.index >= 0 && err.index < paymentRows.length) {
                rowIndex = err.index;
            } else if (err.contractId != null) {
                rowIndex = paymentRows.findIndex(row => row.dbContractId === String(err.contractId));
            }
            if (rowIndex >= 0) {
                next[rowIndex] = err.error;
            }
        }
        return next;
    };

    // Mirrors handleConfirmPayment's bank-account error formatting: turns the backend's per-payment
    // errors into a single numbered, human-readable toast message (falls back to the generic
    // message when there are no structured errors to format).
    const formatBackendPaymentErrorsToast = (
        errors: Array<{ index?: number; contractId?: string | number; error?: string }> | undefined,
        fallbackMessage: string,
    ): string => {
        if (!Array.isArray(errors) || errors.length === 0) return fallbackMessage;
        const getAspireId = (contractId: string) => {
            const contract = activeContracts.find(c => c.Id?.toString() === contractId || c.AspireId === contractId);
            return contract?.AspireId || contractId;
        };
        if (errors.length === 1) {
            const item = errors[0];
            const contractDisplay = item.contractId != null ? getAspireId(String(item.contractId)) : '';
            return contractDisplay ? `Contract ID ${contractDisplay} - ${item.error}` : item.error || fallbackMessage;
        }
        return errors
            .map((item, idx) => {
                const contractDisplay = item.contractId != null ? getAspireId(String(item.contractId)) : '';
                return contractDisplay
                    ? `${idx + 1}. Contract ID ${contractDisplay} - ${item.error}`
                    : `${idx + 1}. ${item.error}`;
            })
            .join('\n\n');
    };

    const handlePayWithCard = async () => {
        if (paymentRows?.length === 0) return;

        setPaymentSubmitErrors([]);
        const requiredFieldErrors = buildRequiredFieldErrors(row => !row?.paymentCardId || row.paymentCardId === '');
        if (!validateRequiredFieldsOrShowError(requiredFieldErrors)) {
            return;
        }

        // Block card payment for any contract with AutoPay enabled (bank transfer only for those)
        if (!validateNoAutoPayForCardPayment(paymentRows)) {
            return;
        }

        // Same amount-vs-outstanding-balance check the bank/ACH flow already runs (handleMakePayment)
        // — card payments were skipping this, so a bad/prefilled custom amount would sail past the
        // frontend and only get caught by the backend's outstanding-balance/invoice-sum validators.
        if (!validateAllRowAmountsWithinMax(paymentRows)) {
            return;
        }

        // Validate minimum card payment amount ($300 minimum for credit card payments)
        if (!validateCardPaymentMinimumAmount(paymentRows)) {
            return;
        }

        // Validate all selected cards are credit cards (not debit/prepaid)
        // Re-fetch payment methods to ensure we have latest funding/country info from Stripe
        const refreshedPaymentMethods = await (async () => {
            try {
                const response = await getStripePaymentMethods(false, { type: 'card', limit: 50 });
                return Array.isArray(response?.data) ? response.data : [];
            } catch (err) {
                console.error('[usePaymentSubmission] Failed to refresh payment methods:', err);
                return [];
            }
        })();

        for (const row of paymentRows) {
            const paymentMethod =
                refreshedPaymentMethods.find((pm: StripePaymentMethodItem) => pm.id === row.paymentCardId) ||
                savedPaymentMethods.find((pm: StripePaymentMethodItem) => pm.id === row.paymentCardId);

            console.log(
                '[usePaymentSubmission] Card validation - payment method:',
                paymentMethod?.id,
                'funding:',
                paymentMethod?.funding,
                'country:',
                paymentMethod?.country,
                'type:',
                paymentMethod?.type,
            );

            if (paymentMethod && paymentMethod.type === 'card') {
                // Only accept explicitly credit cards; reject debit, prepaid, and unknown funding types
                if (paymentMethod.funding !== 'credit') {
                    console.log('[usePaymentSubmission] Rejecting non-credit card. Funding:', paymentMethod.funding);
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: t('makePayment.cardNotEligibleOnlyCredit'),
                            severity: 'error',
                        }),
                    );
                    return;
                }
                // INTERNATIONAL CARD SUPPORT: Currently disabled for domestic-only payments.
                // To enable international cards with 1% FX surcharge, remove this validation block.
                // The fee calculation in useCardFees already supports FX surcharges for international cards.
                if (paymentMethod.country && paymentMethod.country !== 'US') {
                    console.log('[usePaymentSubmission] Rejecting non-US card. Country:', paymentMethod.country);
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: t('makePayment.cardNotEligibleUSOnly'),
                            severity: 'error',
                        }),
                    );
                    return;
                }
            }
        }

        dispatch(showLoader());
        const userId = userDetails?.Id ?? userDetails?.UserId ?? 'user';
        const timestamp = Date.now();
        const payload = {
            currency: 'usd',
            payments: paymentRows.map((row, index) => {
                const contract = getContractById(row.contractId) || getContractById(row.dbContractId);
                const amountDollars = getEffectivePaymentAmount(row, contract ?? undefined);
                const paymentId = `PAY-${String(index + 1).padStart(3, '0')}`;

                // Calculate processing fee using exact formula: T = (x + 0.30) / 0.971
                const STRIPE_FIXED_DOLLARS = 0.3;
                const STRIPE_NET_RATE = 0.971;
                const totalCharge = (amountDollars + STRIPE_FIXED_DOLLARS) / STRIPE_NET_RATE;
                const calculatedFee = Math.round((totalCharge - amountDollars) * 100) / 100;
                const additionalFee = Number.isFinite(cardAdditionalFees) ? cardAdditionalFees : 0;
                const estimatedFeeDollars = parseFloat((calculatedFee + additionalFee).toFixed(2));
                // const estimatedFeeDollars = 65.0;

                return {
                    contractId: Number(row.dbContractId),
                    amount: parseFloat(amountDollars.toFixed(2)),
                    platformFee: parseFloat(estimatedFeeDollars.toFixed(2)),
                    metadata: {
                        contractId: String(row.dbContractId),
                        paymentId,
                    },
                    description: `Contract ${row.contractId} payment`,
                    paymentMethodId: row.paymentCardId,
                };
            }),
            idempotencyKeyPrefix: `bulk_${userId}_${timestamp}_`,
        };

        try {
            const response = await createBulkStripePaymentIntents(payload, false);
            const intentsToConfirm = response?.paymentIntents ?? [];

            if ((response?.statusCode && response.statusCode >= 400) || !Array.isArray(intentsToConfirm)) {
                const errorMessage = response?.message || t('makePayment.paymentCouldNotBeCompleted');
                paymentRows.forEach(row => {
                    trackPaymentFailed(row.contractId, errorMessage, paymentType);
                });
                setPaymentSubmitErrors(mapBackendErrorsToRows(response?.errors));
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: formatBackendPaymentErrorsToast(response?.errors, errorMessage),
                        severity: 'error',
                    }),
                );
                return;
            }

            // Check for eligibility issues
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const ineligibleRows = response?.data?.payments?.filter((p: any) => !p.eligible) || [];
            if (ineligibleRows.length > 0) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const achOnlyRows = ineligibleRows.filter((p: any) => p.paymentOption === 'ACH_ONLY');
                if (achOnlyRows.length > 0) {
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: t('makePayment.cardProcessingFeeUnavailableInState'),
                            severity: 'warning',
                        }),
                    );
                    dispatch(hideLoader());
                    // TODO: Show ACH fallback CTA (or automatically switch to bank_account mode for those rows)
                    return;
                }
                // Handle other ineligibility reasons similarly
                dispatch(hideLoader());
                return;
            }

            if (intentsToConfirm.length === 0) {
                trackAndShowCardPaymentSuccess();
                return;
            }

            const stripe = await stripePromise;
            if (!stripe) {
                console.error(
                    '[usePaymentSubmission] Stripe.js failed to load. Check that StripePublishableKey is set in brand API response.',
                );
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: t('makePayment.stripeNotConfigured'),
                        severity: 'error',
                    }),
                );
                return;
            }

            // Confirm every intent even if one fails — the backend already created a PaymentIntent
            // for each row, so stopping early would leave later rows unattempted despite Stripe
            // being ready to charge them. Results are collected per row instead of bailing out on
            // the first error, so a partial outcome can be reported and handled accurately.
            const returnUrl = `${window.location.origin}${window.location.pathname}`;
            const confirmResults: Array<{ index: number; success: boolean; message?: string }> = [];
            for (let index = 0; index < intentsToConfirm.length; index++) {
                const intent = intentsToConfirm[index];
                const paymentMethodId = paymentRows[index]?.paymentCardId;
                if (!intent?.clientSecret) continue;

                const { error } = await stripe.confirmCardPayment(intent.clientSecret, {
                    payment_method: paymentMethodId || undefined,
                    return_url: returnUrl,
                });
                confirmResults.push({ index, success: !error, message: error?.message });
            }

            const failedResults = confirmResults.filter(r => !r.success);
            const succeededResults = confirmResults.filter(r => r.success);

            if (failedResults.length === 0) {
                trackAndShowCardPaymentSuccess();
                return;
            }

            succeededResults.forEach(r => {
                const row = paymentRows[r.index];
                if (!row) return;
                const contract = getContractById(row.contractId) || getContractById(row.dbContractId);
                trackPaymentCompleted(
                    getEffectivePaymentAmount(row, contract ?? undefined),
                    row.contractId,
                    'Card',
                    paymentType,
                );
            });
            failedResults.forEach(r => {
                const row = paymentRows[r.index];
                if (!row) return;
                trackPaymentFailed(row.contractId, r.message ?? t('makePayment.paymentFailedTryAgain'), paymentType);
            });

            setPaymentSubmitErrors(prev => {
                const next = paymentRows.map((_, i) => prev[i] ?? null);
                failedResults.forEach(r => {
                    next[r.index] = r.message ?? t('makePayment.paymentFailedTryAgain');
                });
                return next;
            });

            if (succeededResults.length === 0) {
                // All confirmations failed — nothing charged, nothing to refresh.
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message:
                            confirmResults.length === 1
                                ? (failedResults[0]?.message ?? t('makePayment.paymentConfirmationFailed'))
                                : t('makePayment.paymentConfirmationFailed'),
                        severity: 'error',
                    }),
                );
                return;
            }

            // Partial outcome: some rows were actually charged. Refresh contracts/open-invoices/
            // scheduled-payments so due amounts and "already scheduled" banners reflect that,
            // since we're staying on this page instead of navigating away like the full-success path.
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: `${succeededResults.length} of ${confirmResults.length} payment(s) succeeded, ${failedResults.length} failed. Please review and retry the failed payment(s) below.`,
                    severity: 'warning',
                }),
            );
            await refreshPaymentData();
        } catch (error) {
            console.error('Bulk card payment error:', error);
            // The axios response interceptor (api-service.ts) already unwraps error responses to
            // error.response.data, so the rejected value here IS the backend body directly
            // ({ message, errors }) — not nested under another .response.data.
            const cardErr = error as {
                message?: string;
                errors?: Array<{ index?: number; contractId?: string | number; error?: string }>;
                response?: {
                    data?: {
                        message?: string;
                        errors?: Array<{ index?: number; contractId?: string | number; error?: string }>;
                    };
                };
            };
            const backendErrors = cardErr?.errors ?? cardErr?.response?.data?.errors;
            setPaymentSubmitErrors(mapBackendErrorsToRows(backendErrors));
            const fallbackMessage =
                cardErr?.response?.data?.message ?? cardErr?.message ?? t('makePayment.paymentFailedTryAgain');
            paymentRows.forEach(row => trackPaymentFailed(row.contractId, fallbackMessage, paymentType));
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: formatBackendPaymentErrorsToast(backendErrors, fallbackMessage),
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleCloseConfirmationModal = () => {
        setShowConfirmationModal(false);
    };

    const fetchUserNotificationsCount = async () => {
        const response = (await getUserNotificationsCount()) as {
            statusCode?: number;
            data?: { unreadCount?: number };
        };
        if (response.statusCode === 200) {
            dispatch(setUserNotificationsCount(response?.data?.unreadCount ?? 0));
        }
    };

    const handlePaymentSuccess = async () => {
        await fetchUserNotificationsCount();
    };

    const handleConfirmPayment = async () => {
        if (impersonation?.isImpersonating) {
            return;
        }

        try {
            dispatch(showLoader());
            // Revalidate: amount cannot exceed allowed max
            if (!validateAllRowAmountsWithinMax(paymentRows ?? [])) {
                return;
            }

            const bulkPaymentPayload = paymentRows?.map(row => {
                const contract = activeContracts.find(
                    (c: ContractApiResponse) => c.Id?.toString() === row.dbContractId || c.AspireId === row.contractId,
                );
                const contractIdForApi = getDbContractIdForPayload(row);
                return {
                    contractId: contractIdForApi,
                    amount: getEffectivePaymentAmount(row, contract ?? undefined),
                    paymentDate: row?.paymentDate ? `${row?.paymentDate.format('YYYY-MM-DD')}T00:00:00.000Z` : '',
                    bankAccountId: row?.bankAccount,
                };
            });

            const invalidRows = bulkPaymentPayload?.filter(
                item => !item.contractId || String(item.contractId).trim() === '',
            );
            if (invalidRows && invalidRows.length > 0) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message:
                            'One Or More Contracts Could Not Be Identified. Please Remove And Re-Add The Contract(s) And Try Again.',
                        severity: 'error',
                    }),
                );
                return;
            }

            const response: CreateBulkPaymentResponse = await makeBulkPayment(bulkPaymentPayload);

            // Number of contracts in this bulk payment (use payload length as source of truth for singular/plural messaging)
            const payloadContractCount = bulkPaymentPayload?.length ?? 0;
            const errorCount = response?.data?.failed ?? 0;
            const errorsArray = response?.errors ?? response?.data?.errors;
            const hasErrors = errorCount > 0 || (Array.isArray(errorsArray) && errorsArray.length > 0);

            console.log('[handleConfirmPayment] Response:', response);
            console.log('[handleConfirmPayment] errorsArray:', errorsArray);
            console.log('[handleConfirmPayment] hasErrors:', hasErrors);
            const successCount = response?.data?.successful ?? 0;
            const totalCount = response?.data?.total ?? payloadContractCount;

            // Determine toast severity and message based on error count
            if (hasErrors) {
                // Track failed payments
                paymentRows?.forEach(row => {
                    trackPaymentFailed(row.dbContractId, response?.message || 'Payment failed', paymentType);
                });

                // If there are detailed errors, format and show them
                if (Array.isArray(errorsArray) && errorsArray.length > 0) {
                    console.log('[handleConfirmPayment] Formatting detailed errors:', errorsArray);
                    const errors = errorsArray;
                    // Helper to get AspireId from dbContractId
                    const getAspireId = (dbContractId: string | number) => {
                        const dbId = String(dbContractId);
                        const contract = activeContracts.find(
                            c => c.Id?.toString() === dbId || c.dbContractId === dbId,
                        );
                        return contract?.AspireId || dbId;
                    };
                    let formattedErrors = '';
                    if (errors.length === 1) {
                        const item = errors[0];
                        const contractDisplay = item.contractId ? getAspireId(item.contractId) : '';
                        formattedErrors = contractDisplay
                            ? `Contract ID ${contractDisplay} - ${item.error || 'Payment failed'}`
                            : item.error || 'Payment failed';
                    } else {
                        formattedErrors = errors
                            .map((item, idx) => {
                                const contractDisplay = item.contractId ? getAspireId(item.contractId) : '';
                                return contractDisplay
                                    ? `${idx + 1}. Contract ID ${contractDisplay} - ${item.error || 'Payment failed'}`
                                    : `${idx + 1}. ${item.error || 'Payment failed'}`;
                            })
                            .join('\n\n');
                    }
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: formattedErrors,
                            severity: 'error',
                        }),
                    );
                } else if (errorCount === totalCount || successCount === 0) {
                    // If all payments failed
                    const isSingleContract = totalCount === 1 || payloadContractCount === 1;
                    // Prefer single-contract message when only 1 contract (ignore backend "Bulk payment processing completed..." message)
                    const errorMessage = isSingleContract
                        ? 'Transaction Failed For 1 Contract.'
                        : response?.message || 'All Payments Failed To Process';
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: errorMessage,
                            severity: 'error',
                        }),
                    );
                } else {
                    // Partial failures - always use frontend message so we never show backend "Bulk payment processing completed..."
                    const errorMessage =
                        successCount === 1 && errorCount === 1
                            ? '1 Transaction Succeeded, 1 Transaction Failed.'
                            : `${successCount} Transaction(s) Succeeded, ${errorCount} Transaction(s) Failed.`;
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: errorMessage,
                            severity: 'error',
                        }),
                    );
                }
            } else if (response?.success || response?.statusCode === 200 || response?.statusCode === 201) {
                // Track successful payments
                paymentRows?.forEach(row => {
                    const contract = getContractById(row.contractId) || getContractById(row.dbContractId);
                    trackPaymentCompleted(
                        getEffectivePaymentAmount(row, contract ?? undefined),
                        row.contractId,
                        'Bank Account',
                        paymentType,
                    );
                });

                // All payments succeeded – prefer frontend message for single contract (avoid backend "Bulk payment..." wording)
                const isSingleContract = totalCount === 1 || payloadContractCount === 1;
                let successMessage: string;
                if (isSingleContract) {
                    successMessage = 'Payment Completed Successfully For The Selected Contract.';
                } else if (totalCount > 1) {
                    successMessage =
                        response?.message || `Transaction Successfully Completed For ${totalCount} Contracts.`;
                } else {
                    successMessage = response?.message || 'Payment(s) Created Successfully';
                }

                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: successMessage,
                        severity: 'success',
                    }),
                );
                setShowConfirmationModal(false);
                navigate(NavigationConfig.Payments, { state: { activeTab: 1 } });
                handlePaymentSuccess();
            } else {
                // General failure case – prefer single-contract message when only 1 contract
                const isSingleContract =
                    (response?.data?.total ?? payloadContractCount) === 1 || payloadContractCount === 1;
                const generalMessage = isSingleContract
                    ? 'Transaction Failed For 1 Contract.'
                    : response?.message || 'Failed To Create Payment(s)';
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: generalMessage,
                        severity: 'error',
                    }),
                );
            }
        } catch (error) {
            console.error('Error creating bulk payment:', error);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const bulkErr = error as any;
            console.log('[handleConfirmPayment] Catch block - bulkErr:', bulkErr);
            console.log('[handleConfirmPayment] Catch block - bulkErr.response?.data:', bulkErr?.response?.data);
            console.log('[handleConfirmPayment] Catch block - bulkErr.errors:', bulkErr?.errors);
            const isSingleContract = (paymentRows?.length ?? 0) === 1;

            // Check if the error response has structured errors
            const errorsList = bulkErr?.errors || bulkErr?.response?.data?.errors;
            let errorMessage = '';
            if (Array.isArray(errorsList) && errorsList.length > 0) {
                console.log('[handleConfirmPayment] Found structured errors:', errorsList);
                const item = errorsList[0];
                if (item.contractId) {
                    const contract = activeContracts.find(c => c.Id?.toString() === String(item.contractId));
                    const contractDisplay = contract?.AspireId || item.contractId;
                    errorMessage = `Contract ID ${contractDisplay} - ${item.error || 'Payment failed'}`;
                } else {
                    errorMessage = item.error || 'Payment failed';
                }
            } else {
                errorMessage = isSingleContract
                    ? 'Transaction Failed For 1 Contract.'
                    : bulkErr?.response?.data?.message || bulkErr?.message || 'Failed To Create Payment(s)';
            }
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: errorMessage,
                    severity: 'error',
                }),
            );
            setShowConfirmationModal(false); // Hide modal on API failure
        } finally {
            dispatch(hideLoader());
        }
    };

    return {
        showConfirmationModal,
        setShowConfirmationModal,
        handleMakePayment,
        handlePayWithCard,
        handleConfirmPayment,
        handleCloseConfirmationModal,
    };
};
